import type { CSSProperties } from "react";
import { LoadingGlobeStage } from "./LoadingGlobeStage";
import { LoadingStatusCard } from "./LoadingStatusCard";
import {
  loadingEnvironmentPhrase,
  loadingEnvironmentStyle,
  readLoadingEnvironment,
} from "./loading-environment";

interface LoadingBootScreenProps {
  phrase?: string;
  compact?: boolean;
  mobileEclipse?: boolean;
}

export function LoadingBootScreen({
  phrase,
  compact = false,
  mobileEclipse = false,
}: LoadingBootScreenProps) {
  const loadingContext = readLoadingEnvironment();

  return (
    <div
      className="loading-boot-screen"
      data-loading-boot-screen="true"
      style={{ ...shellStyle, ...loadingEnvironmentStyle(loadingContext) }}
    >
      <style>{loadingBootCss}</style>
      <div style={environmentOverlayStyle} />
      <div style={scanlineStyle} />
      <LoadingGlobeStage
        phase={mobileEclipse ? "auth" : "idle"}
        density={mobileEclipse ? 0.82 : 1}
        className="loading-boot-screen__globe"
      />
      <div className="loading-boot-screen__status">
        <LoadingStatusCard
          phrase={phrase ?? loadingEnvironmentPhrase(loadingContext)}
          compact={compact}
          bare
          loadingContext={loadingContext}
        />
      </div>
    </div>
  );
}

const shellStyle: CSSProperties = {
  position: "relative",
  minHeight: "100dvh",
  width: "100%",
  overflow: "hidden",
  background: "var(--fintheon-bg, #050402)",
};

const loadingBootCss = `
  .loading-boot-screen__status {
    position: absolute;
    z-index: 2;
    left: 50%;
    bottom: max(5.25rem, calc(14svh + env(safe-area-inset-bottom)));
    width: min(25rem, calc(100vw - 3rem));
    pointer-events: none;
    transform: translateX(-50%);
  }
  .loading-boot-screen .loading-boot-screen__globe {
    z-index: 1;
  }

  @media (max-height: 700px) {
    .loading-boot-screen__status {
      bottom: max(2.5rem, calc(8svh + env(safe-area-inset-bottom)));
    }
  }
`;

const scanlineStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  zIndex: 0,
  background:
    "repeating-linear-gradient(0deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 3px), radial-gradient(circle at center, transparent 0 42%, rgba(0,0,0,0.56) 78%)",
  mixBlendMode: "screen",
  opacity: 0.28,
  pointerEvents: "none",
};

const environmentOverlayStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  zIndex: 0,
  background: "var(--fintheon-loading-overlay)",
  opacity: 0.82,
  pointerEvents: "none",
};
